import {
  cloneElement,
  type ComponentPropsWithRef,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { useTooltip } from './TooltipProvider';

type Props = {
  children: ReactElement;
  tooltipNode: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  offset?: {
    x: number;
    y: number;
  };
};

const Tooltip = (
  props: Props & ComponentPropsWithRef<'div'>,
  ref?: ForwardedRef<HTMLElement>,
) => {
  const { showTooltip, hideTooltip } = useTooltip();
  const { children, tooltipNode, shortcut, disabled, offset, style, ...rest } =
    props;

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      showTooltip(
        tooltipNode,
        e.currentTarget as HTMLElement,
        shortcut,
        offset,
        style,
      );
    },
    [disabled, showTooltip, tooltipNode, shortcut, offset, style],
  );

  useEffect(() => {
    return () => {
      hideTooltip();
    };
  }, [hideTooltip]);

  const clonedAnchor = useMemo(() => {
    return cloneElement(children, {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: hideTooltip,
      ref: ref,
      ...rest,
    });
  }, [children, handleMouseEnter, hideTooltip, ref, rest]);

  return clonedAnchor;
};

export default forwardRef(Tooltip);
